import { useMemo, useState } from "react";
import {
  conflictCount,
  mergeResolved,
  serializeWithMarkers,
  type MergeSegment,
  type Resolution,
} from "../../lib/conflict-merge";

interface ConflictViewProps {
  segments: MergeSegment[];
  fromLabel: string;
  toLabel: string;
  onApply: (merged: string) => void;
  onCopy?: (text: string, label: string) => void;
}

/** Trim leading/trailing blank lines for display without altering merge output. */
function trimBlankLines(s: string): string {
  return s.replace(/^\n+/, "").replace(/\n+$/, "");
}

function ContextBlock({ text }: { text: string }) {
  const [expanded, setExpanded] = useState(false);
  const display = trimBlankLines(text);
  if (!display) return null;
  const lines = display.split("\n");
  if (lines.length <= 6 || expanded) {
    return (
      <pre style={styles.context}>
        {display}
        {expanded && lines.length > 6 && (
          <button
            type="button"
            style={styles.collapseBtn}
            onClick={() => setExpanded(false)}
          >
            collapse
          </button>
        )}
      </pre>
    );
  }
  const head = lines.slice(0, 2).join("\n");
  const tail = lines.slice(-2).join("\n");
  return (
    <pre style={styles.context}>
      {head}
      {"\n"}
      <button
        type="button"
        style={styles.expandBtn}
        onClick={() => setExpanded(true)}
      >
        … {lines.length - 4} unchanged lines …
      </button>
      {"\n"}
      {tail}
    </pre>
  );
}

export function ConflictView({
  segments,
  fromLabel,
  toLabel,
  onApply,
  onCopy,
}: ConflictViewProps) {
  const [resolutions, setResolutions] = useState<Record<string, Resolution>>(
    {},
  );
  const total = useMemo(() => conflictCount(segments), [segments]);
  const resolved = Object.keys(resolutions).length;

  const choose = (id: string, res: Resolution) =>
    setResolutions((m) => ({ ...m, [id]: res }));

  const choices: Array<{ key: Resolution; label: string }> = [
    { key: "ours", label: `keep ${fromLabel}` },
    { key: "theirs", label: `keep ${toLabel}` },
    { key: "both", label: "keep both" },
  ];

  const merged = () => mergeResolved(segments, resolutions, "ours");

  return (
    <div style={styles.wrap}>
      <div style={styles.legend}>
        <span>
          <span style={styles.oursDot} /> ours · {fromLabel}
        </span>
        <span>
          <span style={styles.theirsDot} /> theirs · {toLabel}
        </span>
        <span style={styles.legendCount}>
          {resolved}/{total} resolved
        </span>
      </div>

      <div style={styles.doc}>
        {segments.map((seg, i) => {
          if (seg.type === "text")
            return <ContextBlock key={`t${i}`} text={seg.text} />;
          const choice = resolutions[seg.id] ?? "ours";
          const showOurs = choice === "ours" || choice === "both";
          const showTheirs = choice === "theirs" || choice === "both";
          return (
            <div key={seg.id} style={styles.hunk}>
              <div style={styles.marker}>{`<<<<<<< ${fromLabel}`}</div>
              <pre
                style={{
                  ...styles.side,
                  ...styles.ours,
                  ...(showOurs ? {} : styles.dim),
                }}
              >
                {trimBlankLines(seg.ours) || "(empty)"}
              </pre>
              <div style={styles.marker}>=======</div>
              <pre
                style={{
                  ...styles.side,
                  ...styles.theirs,
                  ...(showTheirs ? {} : styles.dim),
                }}
              >
                {trimBlankLines(seg.theirs) || "(empty)"}
              </pre>
              <div style={styles.marker}>{`>>>>>>> ${toLabel}`}</div>
              <div style={styles.choices}>
                {choices.map((c) => (
                  <button
                    key={c.key}
                    type="button"
                    onClick={() => choose(seg.id, c.key)}
                    style={{
                      ...styles.choice,
                      ...(choice === c.key ? styles.choiceActive : {}),
                    }}
                  >
                    {c.label}
                  </button>
                ))}
              </div>
            </div>
          );
        })}
      </div>

      <div style={styles.footer}>
        <span style={styles.footerCount}>
          {total} conflict{total === 1 ? "" : "s"} · {resolved} resolved
          {resolved < total
            ? ` · ${total - resolved} default to ${fromLabel}`
            : ""}
        </span>
        <div style={styles.footerActions}>
          {onCopy && (
            <button
              type="button"
              style={styles.textBtn}
              onClick={() =>
                onCopy(
                  serializeWithMarkers(
                    segments,
                    fromLabel,
                    toLabel,
                    resolutions,
                  ),
                  "markers copied",
                )
              }
            >
              copy with markers
            </button>
          )}
          <button
            type="button"
            style={styles.textBtn}
            onClick={() => onCopy?.(merged(), "merged copied")}
          >
            copy merged
          </button>
          <button
            type="button"
            style={styles.applyBtn}
            onClick={() => onApply(merged())}
          >
            apply merge
          </button>
        </div>
      </div>
    </div>
  );
}

const styles: Record<string, React.CSSProperties> = {
  wrap: { display: "flex", flexDirection: "column", gap: 0 },
  legend: {
    display: "flex",
    alignItems: "center",
    gap: "var(--space-4)",
    fontSize: 11,
    fontFamily: "var(--font-mono)",
    color: "var(--fg-3)",
    paddingBottom: "var(--space-2)",
    borderBottom: "1px solid var(--comp-rule)",
    marginBottom: "var(--space-3)",
  },
  legendCount: { marginLeft: "auto", color: "var(--fg-mute)" },
  oursDot: {
    display: "inline-block",
    width: 8,
    height: 8,
    marginRight: 6,
    background: "var(--comp-salmon)",
    verticalAlign: "middle",
  },
  theirsDot: {
    display: "inline-block",
    width: 8,
    height: 8,
    marginRight: 6,
    background: "var(--comp-green)",
    verticalAlign: "middle",
  },
  doc: { display: "flex", flexDirection: "column" },
  context: {
    fontFamily: "var(--font-mono)",
    fontSize: 12,
    lineHeight: 1.6,
    color: "var(--fg-2)",
    whiteSpace: "pre-wrap",
    wordBreak: "break-word",
    margin: "4px 0",
  },
  expandBtn: {
    fontFamily: "var(--font-mono)",
    fontSize: 11,
    color: "var(--fg-mute)",
    background: "none",
    border: "none",
    padding: 0,
    cursor: "pointer",
    textDecoration: "underline",
    textUnderlineOffset: 2,
  },
  collapseBtn: {
    display: "block",
    marginTop: 4,
    fontFamily: "var(--font-mono)",
    fontSize: 11,
    color: "var(--fg-mute)",
    background: "none",
    border: "none",
    padding: 0,
    cursor: "pointer",
    textDecoration: "underline",
    textUnderlineOffset: 2,
  },
  hunk: {
    margin: "8px 0",
    border: "1px solid var(--comp-rule)",
    borderRadius: "var(--radius-md)",
    overflow: "hidden",
  },
  marker: {
    fontFamily: "var(--font-mono)",
    fontSize: 11,
    color: "var(--fg-mute)",
    padding: "3px var(--space-3)",
    background: "var(--comp-hover)",
  },
  side: {
    fontFamily: "var(--font-mono)",
    fontSize: 12,
    lineHeight: 1.6,
    whiteSpace: "pre-wrap",
    wordBreak: "break-word",
    margin: 0,
    padding: "6px var(--space-3)",
    borderLeft: "2px solid transparent",
  },
  ours: { borderLeftColor: "var(--comp-salmon)", color: "var(--fg-1)" },
  theirs: { borderLeftColor: "var(--comp-green)", color: "var(--fg-1)" },
  dim: { opacity: 0.4 },
  choices: {
    display: "flex",
    gap: "var(--space-2)",
    padding: "6px var(--space-3)",
    borderTop: "1px solid var(--comp-rule)",
  },
  choice: {
    fontFamily: "var(--font-mono)",
    fontSize: 11,
    color: "var(--fg-3)",
    background: "none",
    border: "none",
    borderBottom: "2px solid transparent",
    borderRadius: 0,
    padding: "1px 2px",
    cursor: "pointer",
  },
  choiceActive: {
    color: "var(--fg-1)",
    fontWeight: 600,
    borderBottomColor: "var(--fg-1)",
  },
  footer: {
    position: "sticky",
    bottom: 0,
    display: "flex",
    alignItems: "center",
    gap: "var(--space-3)",
    padding: "var(--space-3) 0",
    marginTop: "var(--space-3)",
    borderTop: "1px solid var(--comp-rule)",
    background: "var(--comp-paper)",
  },
  footerCount: {
    fontFamily: "var(--font-mono)",
    fontSize: 11,
    color: "var(--fg-3)",
  },
  footerActions: {
    marginLeft: "auto",
    display: "flex",
    alignItems: "center",
    gap: "var(--space-3)",
  },
  textBtn: {
    fontFamily: "var(--font-mono)",
    fontSize: 11,
    color: "var(--fg-2)",
    background: "none",
    border: "none",
    padding: "2px 0",
    cursor: "pointer",
    textDecoration: "underline",
    textUnderlineOffset: 2,
  },
  applyBtn: {
    fontFamily: "var(--font-mono)",
    fontSize: 11,
    fontWeight: 600,
    color: "var(--fg-on-deep)",
    background: "var(--comp-ink)",
    border: "none",
    borderRadius: 0,
    padding: "5px 12px",
    cursor: "pointer",
  },
};
